// Never use the parent viewport as the iframe's height: an iframe clips its
// descendants, including portals. Parent page info only identifies its visible slice.
export function visibleFrameRegion(info, frameHeight) {
    const height = Math.max(1, frameHeight);
    if (!info) return { top: 0, height };
    const start = (Number(info.scrollTop) || 0) - (Number(info.offsetTop) || 0);
    const top = Math.min(height - 1, Math.max(0, start));
    const bottom = Math.min(height, Math.max(top + 1, start + (Number(info.clientHeight) || height)));
    return { top, height: Math.max(1, bottom - top) };
}

function listingHeight() {
    const root = document.getElementById("root");
    return Math.max(1, Math.ceil(root
        ? root.getBoundingClientRect().bottom + window.pageYOffset
        : window.innerHeight));
}

export default class EmbedPopupViewport {
    constructor(onChange, getContentHeight) {
        this.onChange = onChange;
        this.getContentHeight = getContentHeight;
    }

    start = () => {
        if (this.active) return;
        this.active = true;
        // The listing lives in #root; popup portals do not. Do not measure body
        // scrollHeight while the overlay is present, or the temporary height sticks.
        this.listingHeight = listingHeight();
        this.preferredHeight = Math.ceil(this.getContentHeight() + 32);
        this.anchor = null;
        this.info = null;
        this.requestedHeight = null;
        this.update();
        window.addEventListener("resize", this.update);
        // contentWindow initializes asynchronously, sometimes after the first open.
        this.connect();
        if (!this.api) this.retry = window.setInterval(this.connect, 100);
    };

    connect = () => {
        if (!this.active || this.api || !window.parentIFrame) return;
        const api = window.parentIFrame;
        // Only suspend if we can also restore sizing. Partial APIs use the fallback.
        if (typeof api.autoResize !== "function" || typeof api.size !== "function") return;
        try {
            api.autoResize(false);
            this.api = api;
        } catch (_) { return; }
        window.clearInterval(this.retry);
        if (typeof api.getPageInfo === "function") {
            const session = {};
            this.session = session;
            try {
                api.getPageInfo(info => {
                    if (!this.active || this.session !== session) return;
                    this.info = info;
                    this.update();
                });
            } catch (_) { /* A fixed, iframe-bounded popup remains usable. */ }
        }
    };

    update = () => {
        if (!this.active) return;
        const info = this.info;
        if (info && this.api) {
            const start = (Number(info.scrollTop) || 0) - (Number(info.offsetTop) || 0);
            if (this.anchor === null) this.anchor = Math.min(this.listingHeight - 1, Math.max(0, start));
            const available = Math.max(1, (Number(info.clientHeight) || window.innerHeight) + Math.min(0, start));
            // Derive every request from the natural listing and the original
            // anchor, never from the height we just requested. Tall listings stay tall.
            const target = this.listingHeight >= this.preferredHeight ? this.listingHeight :
                Math.max(this.listingHeight, this.anchor + Math.min(this.preferredHeight, available));
            if (target !== this.requestedHeight) {
                this.requestedHeight = target;
                if (target !== this.listingHeight || this.expanded) {
                    try {
                        this.api.size(target);
                        this.expanded = target > this.listingHeight;
                    } catch (_) { /* Bound to the actual frame, not the requested size. */ }
                }
            }
        }
        // A size request is asynchronous (and a host can reject it). Always use
        // the actual child viewport, including during the resize acknowledgement.
        this.onChange({
            ...visibleFrameRegion(info, window.innerHeight),
            absolute: !!info
        });
    };

    stop = () => {
        if (!this.active) return;
        this.active = false;
        this.session = null;
        window.clearInterval(this.retry);
        window.removeEventListener("resize", this.update);
        const api = this.api;
        this.api = null;
        if (!api) return;
        if (typeof api.getPageInfo === "function") {
            try { api.getPageInfo(false); } catch (_) {}
        }
        // Remove the overlay from height calculations before resuming observers.
        // Components hide their portal before calling stop (unmount also removes it).
        // A fixed, overflow-clipped portal host keeps even unmount cleanup out of the
        // document's measured height. Restore explicitly; autoResize(true) alone
        // doesn't force a measurement in iframe-resizer 4.x.
        try { api.autoResize(true); } catch (_) {}
        try { api.size(listingHeight()); } catch (_) {}
        this.expanded = false;
    };
}
